/**
 * Resolve the post-login return path after session expiry (401).
 * Shared by SessionMonitor, axios interceptors, and handleResponseError.
 */

export function pathWithoutQuery(path) {
    return (path || '').split('?')[0];
}

function isUsableReturnPath(path, isAuthEntryPath) {
    if (!path) {
        return false;
    }

    if (isAuthEntryPath(path)) {
        return false;
    }

    if (pathWithoutQuery(path) === '/') {
        return false;
    }

    return true;
}

/**
 * @param {object} options
 * @param {string|null|undefined} options.pendingFullPath - in-flight router destination
 * @param {string|null|undefined} options.rememberedFullPath - SessionMonitor._intendedPath
 * @param {string|null|undefined} options.locationFull - current browser or router URL
 * @param {(path: string) => boolean} options.isAuthEntryPath
 * @returns {string|null}
 */
export function resolveSessionExpiryReturnPath({
    pendingFullPath,
    rememberedFullPath,
    locationFull,
    isAuthEntryPath,
}) {
    const location = locationFull ?? '';

    let chosen = pendingFullPath || rememberedFullPath || null;

    // Stale parent remembered path (e.g. /courses/5) while the URL already advanced
    // (e.g. /courses/5/notes). Only when pending navigation is cleared — pending and
    // an in-flight remembered destination must still win over a lagging URL/currentRoute.
    if (!pendingFullPath && rememberedFullPath && isUsableReturnPath(location, isAuthEntryPath)) {
        const rememberedBase = pathWithoutQuery(rememberedFullPath);
        const locationBase = pathWithoutQuery(location);

        if (
            rememberedBase !== '/' &&
            locationBase !== rememberedBase &&
            locationBase.startsWith(`${rememberedBase}/`)
        ) {
            chosen = location;
        }
    }

    if (isUsableReturnPath(chosen, isAuthEntryPath)) {
        return chosen;
    }

    if (isUsableReturnPath(location, isAuthEntryPath)) {
        return location;
    }

    return null;
}
